/**
 * Header mega menu catalogue.
 *
 * The mega menu lists every service of the platform, grouped in the four
 * header categories (home / business / buildings & outdoor / specialized).
 * The groups below mirror `lib/services/registry.ts` one-to-one so the
 * per-category counts rendered in the mega menu footer always match the real
 * service catalogue.
 *
 * `tests/lib/services/mega-menu.test.ts` enforces that invariant: if a service
 * is added, removed or moved in the registry, the test fails until this file
 * is updated as well.
 *
 * Kept free of registry/pricing imports on purpose — the header is a client
 * component and must not pull the whole service registry (and zod) into the
 * bundle of every page.
 */

export type MegaMenuSectionKey =
  | 'megaMenuHome'
  | 'megaMenuBusiness'
  | 'megaMenuBuildings'
  | 'megaMenuSpecialized';

export interface MegaMenuItem {
  /** Key under `common.footer.serviceLabels`. */
  serviceLabelKey: string;
  /** Detail page of the service. */
  href: string;
}

export interface MegaMenuSection {
  labelKey: MegaMenuSectionKey;
  /** Category hub the section links to. */
  href: string;
  items: MegaMenuItem[];
}

/** `common.footer.serviceLabels` key for every service slug. */
const SERVICE_LABEL_KEYS: Record<string, string> = {
  // Residential
  'regular-home-cleaning': 'regularHomeCleaning',
  'move-in-out-cleaning': 'moveInOutCleaning',
  'seasonal-deep-cleaning': 'seasonalDeepCleaning',
  'window-cleaning': 'windowCleaning',
  'textile-cleaning': 'textileCleaning',
  // Commercial
  'office-cleaning': 'officeCleaning',
  'retail-cleaning': 'retailStoreCleaning',
  'restaurant-cafe-cleaning': 'restaurantCafeCleaning',
  'fitness-gym-cleaning': 'fitnessGymCleaning',
  'salon-beauty-cleaning': 'salonBeautyCleaning',
  'medical-practice-cleaning': 'medicalPracticeCleaning',
  'kindergarten-cleaning': 'kindergartenCleaning',
  'care-facility-cleaning': 'careFacilityCleaning',
  'holiday-apartment-cleaning': 'holidayApartmentCleaning',
  'trade-fair-cleaning': 'tradeFairCleaning',
  // Outdoor
  'stairwell-cleaning': 'stairwellCleaning',
  'underground-garage-cleaning': 'undergroundGarageCleaning',
  'facade-cleaning': 'facadeCleaning',
  'graffiti-removal': 'graffitiRemoval',
  'paved-surface-cleaning': 'pavedSurfaceCleaning',
  'gutter-cleaning': 'gutterCleaning',
  'gardening': 'gardening',
  'pool-cleaning': 'poolCleaning',
  // Specialized (+ transport, its own vertical but reachable from the menu)
  'post-construction-cleaning': 'postConstructionCleaning',
  'hoarding-cleaning': 'hoardingCleaning',
  'crime-scene-cleaning': 'crimeSceneCleaning',
  'furniture-transport': 'furnitureTransport',
};

/** Home cleaning — `residentialServices` in the registry (5). */
const HOME_SERVICE_IDS = [
  'regular-home-cleaning',
  'move-in-out-cleaning',
  'seasonal-deep-cleaning',
  'window-cleaning',
  'textile-cleaning',
] as const;

/** Business cleaning — `commercialServices` in the registry (10). */
const BUSINESS_SERVICE_IDS = [
  'office-cleaning',
  'retail-cleaning',
  'restaurant-cafe-cleaning',
  'fitness-gym-cleaning',
  'salon-beauty-cleaning',
  'medical-practice-cleaning',
  'kindergarten-cleaning',
  'care-facility-cleaning',
  'holiday-apartment-cleaning',
  'trade-fair-cleaning',
] as const;

/** Buildings & outdoor — `outdoorServices` in the registry (8). */
const BUILDINGS_SERVICE_IDS = [
  'stairwell-cleaning',
  'underground-garage-cleaning',
  'facade-cleaning',
  'graffiti-removal',
  'paved-surface-cleaning',
  'gutter-cleaning',
  'gardening',
  'pool-cleaning',
] as const;

/** Specialized services — `specializedServices` + `transportServices` (3 + 1). */
const SPECIALIZED_SERVICE_IDS = [
  'post-construction-cleaning',
  'hoarding-cleaning',
  'crime-scene-cleaning',
  'furniture-transport',
] as const;

function toMenuItems(serviceIds: readonly string[]): MegaMenuItem[] {
  return serviceIds.map(serviceId => {
    const serviceLabelKey = SERVICE_LABEL_KEYS[serviceId];
    if (!serviceLabelKey) {
      throw new Error(`Missing serviceLabels translation key for "${serviceId}"`);
    }

    return { serviceLabelKey, href: `/services/${serviceId}` };
  });
}

export const megaMenuSections: MegaMenuSection[] = [
  {
    labelKey: 'megaMenuHome',
    href: '/services/residential',
    items: toMenuItems(HOME_SERVICE_IDS),
  },
  {
    labelKey: 'megaMenuBusiness',
    href: '/services/commercial',
    items: toMenuItems(BUSINESS_SERVICE_IDS),
  },
  {
    labelKey: 'megaMenuBuildings',
    href: '/services/outdoor',
    items: toMenuItems(BUILDINGS_SERVICE_IDS),
  },
  {
    labelKey: 'megaMenuSpecialized',
    href: '/services/specialized',
    items: toMenuItems(SPECIALIZED_SERVICE_IDS),
  },
];

/** Number of services listed in a mega menu section. */
export function getMegaMenuSectionCount(section: MegaMenuSection): number {
  return section.items.length;
}

/** Total number of services reachable from the mega menu. */
export function getMegaMenuTotalCount(): number {
  return megaMenuSections.reduce((total, section) => total + section.items.length, 0);
}
